(() => {
  const KEY = 'lc_cookie_consent';
  const CAT_KEY = 'lc_cookie_consent_categories';

  const banner = document.getElementById('lc-cookie-consent');
  const sheet = document.getElementById('lc-cookie-prefs');
  if (!banner) return;

  // Publish the live banner height as --lc-cookie-bar-h so bottom-anchored
  // overlays (the scrollytelling per-frame credit) can lift clear of the bar
  // while it is showing and drop back once it is dismissed. 0px when hidden.
  const root = document.documentElement;
  function publishBarHeight() {
    root.style.setProperty(
      '--lc-cookie-bar-h',
      (banner.classList.contains('is-visible') ? banner.offsetHeight : 0) + 'px'
    );
  }
  window.addEventListener('resize', publishBarHeight, { passive: true });

  // Already decided -> don't show banner. Sheet stays available via the
  // footer link (a future task) so users can revisit.
  let alreadyDecided = false;
  try {
    alreadyDecided = !!localStorage.getItem(KEY);
  } catch (_e) {
    // iOS private browsing or content blocker — treat as undecided, show banner
  }
  if (!alreadyDecided) {
    requestAnimationFrame(() => {
      banner.hidden = false;
      requestAnimationFrame(() => {
        banner.classList.add('is-visible');
        publishBarHeight();
      });
    });
  }

  // Decide value can be: "accepted" | "declined" | "custom"
  function decide(value, categories) {
    try {
      localStorage.setItem(KEY, value);
      if (categories) {
        localStorage.setItem(CAT_KEY, JSON.stringify(categories));
      }
    } catch (_e) {
      // Private browsing — consent captured in memory only for this session
    }
    banner.classList.remove('is-visible');
    publishBarHeight();
    const hideBanner = () => { banner.hidden = true; };
    banner.addEventListener('transitionend', hideBanner, { once: true });
    // Fallback when no transition runs (reduced motion, or decide()
    // called from the prefs sheet before the banner ever animated) -
    // same belt-and-braces pattern as lc-klaviyo-popup.js / lc-toast.js.
    setTimeout(hideBanner, 400);
    document.dispatchEvent(new CustomEvent('lc:consent', {
      detail: { value, categories: categories || categoriesFor(value) },
    }));

    // Shopify Customer Privacy API integration. Each category maps to
    // the corresponding Shopify field; functional maps to "preferences".
    if (typeof window.Shopify?.customerPrivacy?.setTrackingConsent === 'function') {
      const cats = categories || categoriesFor(value);
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: cats.analytics ? 'yes' : 'no',
        marketing: cats.marketing ? 'yes' : 'no',
        preferences: cats.functional ? 'yes' : 'no',
        sale_of_data: 'no',
      }, () => {});
    }

    // Google Consent Mode v2 update. Lifts the 'denied' defaults set in
    // theme.liquid before content_for_header so GA4 and Google Ads start
    // collecting on acceptance.
    if (typeof window.gtag === 'function') {
      const cats = categories || categoriesFor(value);
      window.gtag('consent', 'update', {
        ad_storage: cats.marketing ? 'granted' : 'denied',
        ad_user_data: cats.marketing ? 'granted' : 'denied',
        ad_personalization: cats.marketing ? 'granted' : 'denied',
        analytics_storage: cats.analytics ? 'granted' : 'denied',
        functionality_storage: cats.functional ? 'granted' : 'denied',
        personalization_storage: cats.functional ? 'granted' : 'denied',
      });
    }
  }

  function categoriesFor(value) {
    if (value === 'accepted') return { analytics: true, marketing: true, functional: true };
    if (value === 'declined') return { analytics: false, marketing: false, functional: false };
    return { analytics: false, marketing: false, functional: false };
  }

  // Banner buttons.
  document.getElementById('lc-cookie-accept')?.addEventListener('click', () => decide('accepted'));
  document.getElementById('lc-cookie-decline')?.addEventListener('click', () => decide('declined'));

  // F·14 — Manage preferences sheet.
  function openSheet() {
    if (!sheet) return;
    // Pre-fill toggles from previously-saved choice if any.
    const saved = (() => {
      try { return JSON.parse(localStorage.getItem(CAT_KEY)) || null; }
      catch (_e) { return null; }
    })();
    if (saved) {
      ['analytics', 'marketing', 'functional'].forEach((cat) => {
        const input = document.getElementById('lc-cookie-cat-' + cat);
        if (input) input.checked = !!saved[cat];
      });
    }
    sheet.hidden = false;
    document.getElementById('lc-cookie-manage')?.setAttribute('aria-expanded', 'true');
    // Move focus to the heading for screen readers.
    sheet.querySelector('.lc-cookie-prefs__heading')?.focus?.();
    document.addEventListener('keydown', sheetKeyHandler);
    trapFocus(sheet);
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.hidden = true;
    document.getElementById('lc-cookie-manage')?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', sheetKeyHandler);
    sheet.removeEventListener('keydown', sheetTrapHandler);
    document.getElementById('lc-cookie-manage')?.focus?.();
  }

  function sheetKeyHandler(e) {
    if (e.key === 'Escape') closeSheet();
  }

  function readToggleState() {
    return {
      analytics: document.getElementById('lc-cookie-cat-analytics')?.checked,
      marketing: document.getElementById('lc-cookie-cat-marketing')?.checked,
      functional: document.getElementById('lc-cookie-cat-functional')?.checked,
    };
  }

  document.getElementById('lc-cookie-manage')?.addEventListener('click', openSheet);
  document.getElementById('lc-cookie-prefs-close')?.addEventListener('click', closeSheet);
  document.getElementById('lc-cookie-prefs-scrim')?.addEventListener('click', closeSheet);
  document.getElementById('lc-cookie-prefs-save')?.addEventListener('click', () => {
    decide('custom', readToggleState());
    closeSheet();
  });
  document.getElementById('lc-cookie-prefs-decline-all')?.addEventListener('click', () => {
    decide('declined', { analytics: false, marketing: false, functional: false });
    closeSheet();
  });

  // Lightweight focus trap inside the sheet while it's open. Named,
  // module-level handler so closeSheet can remove it symmetrically -
  // the previous inline listener stacked a duplicate on every open.
  // Focusables are recomputed per keydown so the list is never stale.
  function sheetTrapHandler(e) {
    if (e.key !== 'Tab' || !sheet) return;
    const focusables = sheet.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  function trapFocus(container) {
    container.removeEventListener('keydown', sheetTrapHandler);
    container.addEventListener('keydown', sheetTrapHandler);
  }

  // If banner was already decided, expose openSheet via global for the
  // (future) footer "Cookie preferences" link.
  window.LCCookiePrefs = { open: openSheet };
})();
